import { RawDraftContentState } from "draft-js";
import { Feature } from "geojson";

import analytics from "fond/analytics";
import { AnalyticsEvents } from "fond/analytics/types";
import * as API from "fond/api";
import { setDraggedPopupPosition } from "fond/project/redux";
import {
  ADD_COMMENT_FAILURE,
  ADD_COMMENT_REQUEST,
  ADD_COMMENT_SUCCESS,
  ADD_REPLY_FAILURE,
  ADD_REPLY_REQUEST,
  ADD_REPLY_SUCCESS,
  CommentsActionType,
  DELETE_COMMENT_FAILURE,
  DELETE_COMMENT_REQUEST,
  DELETE_COMMENT_SUCCESS,
  DELETE_REPLY_FAILURE,
  DELETE_REPLY_REQUEST,
  DELETE_REPLY_SUCCESS,
  FILTER_DELETED_VERSION_COMMENTS,
  GET_COMMENTS_FAILURE,
  GET_COMMENTS_REQUEST,
  GET_COMMENTS_SUCCESS,
  RESOLVE_COMMENT_FAILURE,
  RESOLVE_COMMENT_REQUEST,
  RESOLVE_COMMENT_SUCCESS,
  SELECT_COMMENT,
  SET_EDIT_ID,
  SET_FILTERS,
  SET_MAP_FILTER,
  SET_SEARCH_TEXT,
  SET_SORT_ORDER,
  UPDATE_COMMENT_FAILURE,
  UPDATE_COMMENT_REQUEST,
  UPDATE_COMMENT_SUCCESS,
  UPDATE_REPLY_FAILURE,
  UPDATE_REPLY_REQUEST,
  UPDATE_REPLY_SUCCESS,
} from "fond/redux/comments";
import * as turf from "fond/turf";
import { AppThunk, Comment, CommentFilters, CommentImportance, CommentSortOrder, CommentState, Reply } from "fond/types";
import { getPlainText } from "fond/utils/draftEditor";

const getCommentsRequest = (): CommentsActionType.GetCommentsRequest => ({ type: GET_COMMENTS_REQUEST });
const getCommentsSuccess = (payload: { Comments: Comment[] }): CommentsActionType.GetCommentsSuccess => ({
  type: GET_COMMENTS_SUCCESS,
  payload,
});
const getCommentsFailure = (error: Error): CommentsActionType.GetCommentsFailure => ({ type: GET_COMMENTS_FAILURE, error });
const addCommentRequest = (): CommentsActionType.AddCommentRequest => ({ type: ADD_COMMENT_REQUEST });
const addCommentSuccess = (payload: { Comment: Comment }): CommentsActionType.AddCommentSuccess => ({
  type: ADD_COMMENT_SUCCESS,
  payload,
});
const addCommentFailure = (error: Error): CommentsActionType.AddCommentFailure => ({ type: ADD_COMMENT_FAILURE, error });

const updateCommentRequest = (): CommentsActionType.UpdateCommentRequest => ({ type: UPDATE_COMMENT_REQUEST });
const updateCommentSuccess = (payload: { Comment: Comment }): CommentsActionType.UpdateCommentSuccess => ({
  type: UPDATE_COMMENT_SUCCESS,
  payload,
});
const updateCommentFailure = (error: Error): CommentsActionType.UpdateCommentFailure => ({ type: UPDATE_COMMENT_FAILURE, error });

const resolveCommentRequest = (): CommentsActionType.ResolveCommentRequest => ({ type: RESOLVE_COMMENT_REQUEST });
const resolveCommentSuccess = (payload: { Comment: Comment }): CommentsActionType.ResolveCommentSuccess => ({
  type: RESOLVE_COMMENT_SUCCESS,
  payload,
});
const resolveCommentFailure = (error: Error): CommentsActionType.ResolveCommentFailure => ({ type: RESOLVE_COMMENT_FAILURE, error });

const updateReplyRequest = (): CommentsActionType.UpdateReplyRequest => ({ type: UPDATE_REPLY_REQUEST });
const updateReplySuccess = (payload: { Reply: Reply }): CommentsActionType.UpdateReplySuccess => ({
  type: UPDATE_REPLY_SUCCESS,
  payload,
});
const updateReplyFailure = (error: Error): CommentsActionType.UpdateReplyFailure => ({ type: UPDATE_REPLY_FAILURE, error });

const deleteCommentRequest = (): CommentsActionType.DeleteCommentRequest => ({ type: DELETE_COMMENT_REQUEST });
const deleteCommentSuccess = (payload: { ID: string }): CommentsActionType.DeleteCommentSuccess => ({
  type: DELETE_COMMENT_SUCCESS,
  payload,
});
const deleteCommentFailure = (error: Error): CommentsActionType.DeleteCommentFailure => ({ type: DELETE_COMMENT_FAILURE, error });

const addReplyRequest = (): CommentsActionType.AddReplyRequest => ({ type: ADD_REPLY_REQUEST });
const addReplySuccess = (payload: { Comment: Comment; Reply: Reply }): CommentsActionType.AddReplySuccess => ({
  type: ADD_REPLY_SUCCESS,
  payload,
});
const addReplyFailure = (error: Error): CommentsActionType.AddReplyFailure => ({ type: ADD_REPLY_FAILURE, error });

const deleteReplyRequest = (): CommentsActionType.DeleteReplyRequest => ({ type: DELETE_REPLY_REQUEST });
const deleteReplySuccess = (payload: { ID: string; CommentID: string }): CommentsActionType.DeleteReplySuccess => ({
  type: DELETE_REPLY_SUCCESS,
  payload,
});
const deleteReplyFailure = (error: Error): CommentsActionType.DeleteReplyFailure => ({ type: DELETE_REPLY_FAILURE, error });

export const filterDeletedVersionComments = (payload: { versionID: string }): CommentsActionType.FilterDeletedVersionComments => ({
  type: FILTER_DELETED_VERSION_COMMENTS,
  payload,
});

const selectCommentSuccess = (payload: {
  commentID: string;
  highlightedFeatures: mapboxgl.FeatureIdentifier[] | mapboxgl.MapboxGeoJSONFeature[];
  lngLat: mapboxgl.LngLatLike;
  mapSelection: boolean;
  showPopup: boolean;
}): CommentsActionType.SelectComment => ({ type: SELECT_COMMENT, payload });

/**
 * Requests the all comments for a project
 */
export const getComments =
  (id: string): AppThunk<void> =>
  async (dispatch) => {
    dispatch(getCommentsRequest());
    return API.getComments(id)
      .then((result) => {
        dispatch(getCommentsSuccess(result));
      })
      .catch((e) => {
        dispatch(getCommentsFailure(e));
      });
  };

/**
 * Posts a new comment
 */
export const addComment =
  (comment: Pick<Comment, "Version" | "Features" | "Importance" | "RawContent" | "Type">): AppThunk<Promise<Comment>> =>
  async (dispatch, getState) => {
    dispatch(addCommentRequest());
    const plainText = getPlainText(comment.RawContent);

    return API.addComment({
      Content: plainText,
      RawContent: comment.RawContent,
      Importance: comment.Importance,
      VersionID: comment.Version,
      Features: comment.Features,
      Type: comment.Type,
    })
      .then((result) => {
        dispatch(addCommentSuccess({ Comment: result }));
        analytics.track("project.comment.added", {
          length: plainText.length,
          projectId: getState().project.projectId,
          type: comment.Type,
          importance: comment.Importance,
        });
        return Promise.resolve(result);
      })
      .catch((e: Error) => {
        dispatch(addCommentFailure(e));
        return Promise.reject(e);
      });
  };

export const selectComment =
  ({
    commentID,
    features,
    point,
    showPopup = true,
    isFromFeaturesPopup = false,
  }: {
    commentID: string;
    features: any[];
    point?: mapboxgl.LngLatLike;
    showPopup?: boolean;
    isFromFeaturesPopup?: boolean;
  }): AppThunk<void> =>
  (dispatch) => {
    // If a point was not passed use turf to determine an appropriate lngLat
    // For example the Feature Table can select a feature from the table.
    let lngLat = point;
    if (!lngLat && features.length > 0) {
      const bestPoint = turf.pointOnFeature(features[0]);
      lngLat = {
        lng: bestPoint.geometry.coordinates[0],
        lat: bestPoint.geometry.coordinates[1],
      };
    }

    // Unless the comment is selected from FeaturesPopup, reset the draggable popup states.
    if (!isFromFeaturesPopup) {
      dispatch(setDraggedPopupPosition(null));
    }

    dispatch(
      selectCommentSuccess({
        commentID,
        highlightedFeatures: features.map((feature) => ({
          ...feature,
          id: commentID,
          source: feature.source || `comments-source`,
        })),
        lngLat: lngLat as mapboxgl.LngLatLike,
        // We need to understand if the selection was from the map
        // or the list of comments
        mapSelection: point !== undefined,
        showPopup,
      })
    );
  };

export const addReply =
  ({ rawContent, comment }: { rawContent: RawDraftContentState; comment: Comment }): AppThunk<Promise<Reply>> =>
  async (dispatch, getState) => {
    dispatch(addReplyRequest());
    const plainText = getPlainText(rawContent);
    return API.addReply(comment.ID, {
      Content: plainText,
      RawContent: rawContent,
    })
      .then((result) => {
        dispatch(addReplySuccess({ Reply: result, Comment: comment }));
        analytics.track("project.comment.replied", {
          projectId: getState().project.projectId,
          type: comment.Type,
          importance: comment.Importance,
          length: plainText.length,
          parentId: comment.ID,
        });
        return Promise.resolve(result);
      })
      .catch((e: Error) => {
        dispatch(addReplyFailure(e));
        return Promise.reject(e);
      });
  };

export const deleteComment =
  (comment: Comment): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    dispatch(deleteCommentRequest());

    return API.deleteComment(comment.ID)
      .then(() => {
        dispatch(deleteCommentSuccess({ ID: comment.ID }));
        analytics.track("project.comment.deleted", {
          projectId: getState().project.projectId,
          type: comment.Type,
          importance: comment.Importance,
        });
        return Promise.resolve();
      })
      .catch((e: Error) => {
        dispatch(deleteCommentFailure(e));
        return Promise.reject(e);
      });
  };

export const deleteReply =
  (reply: Reply): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    dispatch(deleteReplyRequest());

    return API.deleteReply(reply.ID)
      .then(() => {
        dispatch(deleteReplySuccess({ ID: reply.ID, CommentID: reply.CommentID }));
        analytics.track("project.comment.reply.deleted", {
          projectId: getState().project.projectId,
          parentId: reply.CommentID,
        });
        return Promise.resolve();
      })
      .catch((e: Error) => {
        dispatch(deleteReplyFailure(e));
        return Promise.reject(e);
      });
  };

export const updateComment =
  ({
    rawContent,
    importance,
    comment,
    features,
  }: {
    rawContent: RawDraftContentState;
    importance: CommentImportance | null;
    comment: Comment;
    features: Feature[];
  }): AppThunk<Promise<Comment>> =>
  async (dispatch, getState) => {
    dispatch(updateCommentRequest());
    const plainText = getPlainText(rawContent);

    return API.updateComment(comment.ID, {
      Content: plainText,
      RawContent: rawContent,
      Importance: importance,
      Features: features,
    })
      .then((result) => {
        dispatch(updateCommentSuccess({ Comment: result }));
        const featuresUpdated: Pick<AnalyticsEvents["project.comment.edited"], "updatedFeature"> = {};
        if (comment.Features !== features) {
          featuresUpdated.updatedFeature = true;
        }
        analytics.track("project.comment.edited", {
          projectId: getState().project.projectId,
          type: comment.Type,
          importance: comment.Importance,
          length: plainText.length,
          ...featuresUpdated,
        });
        return Promise.resolve(result);
      })
      .catch((e: Error) => {
        dispatch(updateCommentFailure(e));
        return Promise.reject(e);
      });
  };

export const updateCommentImportance =
  ({ comment, importance }: { comment: Comment; importance: CommentImportance | null }): AppThunk<Promise<Comment>> =>
  async (dispatch, getState) => {
    dispatch(updateCommentRequest());
    return API.updateComment(comment.ID, {
      Importance: importance,
    })
      .then((result) => {
        dispatch(updateCommentSuccess({ Comment: result }));
        analytics.track("project.comment.importance.changed", {
          projectId: getState().project.projectId,
          type: comment.Type,
          importance: comment.Importance,
          newImportance: importance,
        });
        return Promise.resolve(result);
      })
      .catch((e: Error) => {
        dispatch(updateCommentFailure(e));
        return Promise.reject(e);
      });
  };

export const updateReply =
  ({ rawContent, reply }: { rawContent: RawDraftContentState; reply: Reply }): AppThunk<Promise<Reply>> =>
  async (dispatch, getState) => {
    dispatch(updateReplyRequest());
    const plainText = getPlainText(rawContent);

    return API.updateReply(reply.ID, {
      Content: plainText,
      RawContent: rawContent,
    })
      .then((result) => {
        dispatch(updateReplySuccess({ Reply: result }));
        analytics.track("project.comment.reply.edited", {
          projectId: getState().project.projectId,
          length: plainText.length,
          parentId: reply.CommentID,
        });

        return Promise.resolve(result);
      })
      .catch((e: Error) => {
        dispatch(updateReplyFailure(e));
        return Promise.reject(e);
      });
  };

export const resolveComment =
  (comment: Comment, commentState: CommentState): AppThunk<Promise<Comment>> =>
  async (dispatch, getState) => {
    dispatch(resolveCommentRequest());

    return API.resolveComment(comment.ID, commentState)
      .then((result) => {
        dispatch(resolveCommentSuccess({ Comment: result }));
        analytics.track("project.comment.state.changed", {
          projectId: getState().project.projectId,
          type: comment.Type,
          importance: comment.Importance,
          state: commentState,
        });
        return Promise.resolve(result);
      })
      .catch((e: Error) => {
        dispatch(resolveCommentFailure(e));
        return Promise.reject(e);
      });
  };

export const setEditId = (editId: string | null): CommentsActionType.SetEditId => ({
  type: SET_EDIT_ID,
  editId,
});

export const setMapFilter = (filter: boolean): CommentsActionType.SetMapFilter => ({
  type: SET_MAP_FILTER,
  mapFilter: filter,
});

export const setSortOrder = (order: CommentSortOrder): CommentsActionType.SetSortOrder => ({
  type: SET_SORT_ORDER,
  sortOrder: order,
});

export const setFilters = (filters: CommentFilters): CommentsActionType.SetFilters => ({
  type: SET_FILTERS,
  filters,
});

export const setSearchText = (text: string): CommentsActionType.SetSearchText => ({
  type: SET_SEARCH_TEXT,
  searchText: text,
});
